/**
 * Form-session provenance for fields Copilot entered or changed in native CRUD.
 * Header `products` stays a boolean-excluded key; line cells use copilotModifiedProductLines.
 */

export const COPILOT_PROVENANCE_EXCLUDED_FIELD_IDS = new Set(['products', 'extraFields']);

export const COPILOT_PRODUCT_LINE_EXCLUDED_FIELD_IDS = new Set([
    'currencySymbol',
    'description',
    'extraFields',
    'finalPrice',
    'finalprice',
    'id',
    'idThumbnail',
    'idproduct',
    'isNew',
    'lineDiscountTotalAmount',
    'model',
    'productPrice',
    'products',
    'subtotalAmount',
    'taxRate',
    'thumbnail',
    'totalAmount',
]);

const isEmptyComparableValue = (value) =>
    value === null || value === undefined || value === '' || value === '-1';

const getSelectLikeId = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
    if (Object.prototype.hasOwnProperty.call(value, 'value')) return value.value;
    if (Object.prototype.hasOwnProperty.call(value, 'id')) return value.id;
    return undefined;
};

const getDateTime = (value) => {
    if (value instanceof Date) {
        const time = value.getTime();
        return Number.isNaN(time) ? null : time;
    }

    if (typeof value !== 'string' || !value) return undefined;

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return undefined;
    if (!/^\d{4}-\d{2}-\d{2}/.test(value) && !value.includes('T')) return undefined;

    return parsed.getTime();
};

export const areCrudFieldValuesEqual = (left, right) => {
    if (left === right) return true;
    if (isEmptyComparableValue(left) && isEmptyComparableValue(right)) return true;

    const leftDate = getDateTime(left);
    const rightDate = getDateTime(right);
    if (leftDate !== undefined || rightDate !== undefined) {
        return leftDate === rightDate;
    }

    if (Array.isArray(left) || Array.isArray(right)) {
        const leftItems = Array.isArray(left) ? left : [];
        const rightItems = Array.isArray(right) ? right : [];
        if (leftItems.length !== rightItems.length) return false;
        return leftItems.every((item, index) => areCrudFieldValuesEqual(item, rightItems[index]));
    }

    const leftSelectId = getSelectLikeId(left);
    const rightSelectId = getSelectLikeId(right);
    if (leftSelectId !== undefined || rightSelectId !== undefined) {
        if (isEmptyComparableValue(leftSelectId) && isEmptyComparableValue(rightSelectId)) {
            return true;
        }
        return String(leftSelectId) === String(rightSelectId);
    }

    if (left && right && typeof left === 'object' && typeof right === 'object') {
        if (
            Object.prototype.hasOwnProperty.call(left, 'html') ||
            Object.prototype.hasOwnProperty.call(left, 'text') ||
            Object.prototype.hasOwnProperty.call(right, 'html') ||
            Object.prototype.hasOwnProperty.call(right, 'text')
        ) {
            return (left.html || left.text || '') === (right.html || right.text || '');
        }

        try {
            return JSON.stringify(left) === JSON.stringify(right);
        } catch {
            return false;
        }
    }

    return String(left) === String(right);
};

export const isCopilotProvenanceFieldId = (fieldId, mappedSchema) => {
    if (!fieldId || COPILOT_PROVENANCE_EXCLUDED_FIELD_IDS.has(fieldId)) return false;
    if (mappedSchema && !mappedSchema[fieldId]) return false;
    return true;
};

export const toCopilotModifiedFieldsMap = (fields, mappedSchema) => {
    if (!fields || typeof fields !== 'object') return {};

    return Object.keys(fields).reduce((map, fieldId) => {
        if (isCopilotProvenanceFieldId(fieldId, mappedSchema)) {
            map[fieldId] = true;
        }
        return map;
    }, {});
};

export const sanitizeCopilotModifiedFields = (map, mappedSchema) => {
    if (!map || typeof map !== 'object') return {};

    return Object.keys(map).reduce((next, fieldId) => {
        if (map[fieldId] && isCopilotProvenanceFieldId(fieldId, mappedSchema)) {
            next[fieldId] = true;
        }
        return next;
    }, {});
};

export const nextCopilotModifiedFields = ({
    copilotFieldIds,
    current = {},
    incomingFields = {},
    mappedSchema,
    previousData = {},
} = {}) => {
    const next = sanitizeCopilotModifiedFields(current, mappedSchema);
    const copilotIds = new Set(
        (Array.isArray(copilotFieldIds) ? copilotFieldIds : []).filter((fieldId) =>
            isCopilotProvenanceFieldId(fieldId, mappedSchema),
        ),
    );

    Object.keys(incomingFields || {}).forEach((fieldId) => {
        if (!isCopilotProvenanceFieldId(fieldId, mappedSchema)) return;

        if (copilotIds.has(fieldId)) {
            next[fieldId] = true;
            return;
        }

        if (!areCrudFieldValuesEqual(previousData?.[fieldId], incomingFields[fieldId])) {
            delete next[fieldId];
        }
    });

    return next;
};

export const isCopilotEnrichedField = (
    copilotModifiedFields,
    fieldId,
    { hidden = false, readOnly = false } = {},
) => {
    if (!fieldId || hidden || readOnly) return false;
    return Boolean(copilotModifiedFields?.[fieldId]);
};

export const isCopilotProductLineFieldId = (fieldId) => {
    if (!fieldId) return false;
    if (COPILOT_PRODUCT_LINE_EXCLUDED_FIELD_IDS.has(fieldId)) return false;
    if (String(fieldId).startsWith('_')) return false;
    return true;
};

const markProductLineField = (cellMap, fieldId) => {
    if (!isCopilotProductLineFieldId(fieldId)) return;
    cellMap[fieldId] = true;
    const lower = String(fieldId).toLowerCase();
    if (lower !== fieldId && isCopilotProductLineFieldId(lower)) {
        cellMap[lower] = true;
    }
};

export const getCopilotProductLineFieldIds = (copilotLine) => {
    if (!copilotLine || typeof copilotLine !== 'object') return [];
    return Object.keys(copilotLine).filter((fieldId) => isCopilotProductLineFieldId(fieldId));
};

export const toCopilotModifiedProductLinesMap = (copilotLines, crudLines) => {
    const source = (Array.isArray(copilotLines) ? copilotLines : []).filter(
        (line) => line?.idproduct != null,
    );
    const dest = Array.isArray(crudLines) ? crudLines : [];

    return source.reduce((map, line, index) => {
        const rowId = dest[index]?.id;
        if (rowId == null || rowId === '') return map;

        const cellMap = getCopilotProductLineFieldIds(line).reduce((next, fieldId) => {
            markProductLineField(next, fieldId);
            return next;
        }, {});

        if (Object.keys(cellMap).length) {
            map[rowId] = cellMap;
        }
        return map;
    }, {});
};

export const sanitizeCopilotModifiedProductLines = (map) => {
    if (!map || typeof map !== 'object') return {};

    return Object.keys(map).reduce((next, rowId) => {
        const cellMap = map[rowId];
        if (!cellMap || typeof cellMap !== 'object') return next;

        const nextCells = Object.keys(cellMap).reduce((cells, fieldId) => {
            if (cellMap[fieldId] && isCopilotProductLineFieldId(fieldId)) {
                cells[fieldId] = true;
            }
            return cells;
        }, {});

        if (Object.keys(nextCells).length) {
            next[rowId] = nextCells;
        }
        return next;
    }, {});
};

const indexProductLinesById = (products) => {
    if (!Array.isArray(products)) return {};
    return products.reduce((map, row) => {
        if (row?.id != null && row.id !== '') {
            map[row.id] = row;
        }
        return map;
    }, {});
};

const unmarkProductLineCell = (map, rowId, fieldId) => {
    if (!rowId || !map[rowId] || !isCopilotProductLineFieldId(fieldId)) return;
    const nextCells = { ...map[rowId] };
    delete nextCells[fieldId];
    const lower = String(fieldId).toLowerCase();
    if (lower !== fieldId) delete nextCells[lower];
    if (Object.keys(nextCells).length) {
        map[rowId] = nextCells;
    } else {
        delete map[rowId];
    }
};

const getProductLineFieldValue = (row, fieldId) => {
    if (!row || fieldId == null || fieldId === '') return undefined;
    if (Object.prototype.hasOwnProperty.call(row, fieldId)) return row[fieldId];
    const lower = String(fieldId).toLowerCase();
    if (lower !== fieldId && Object.prototype.hasOwnProperty.call(row, lower)) {
        return row[lower];
    }
    return undefined;
};

export const snapshotProductLineCellEdit = (product, products) => {
    if (!product || typeof product !== 'object' || Array.isArray(product) || !product.field) {
        return undefined;
    }

    const lines = Array.isArray(products) ? products : [];
    const row =
        (product.id != null && product.id !== ''
            ? lines.find((line) => line?.id === product.id)
            : undefined) || lines[product.rowIndex];

    return {
        field: product.field,
        id: product.id ?? row?.id,
        previousValue: getProductLineFieldValue(row, product.field),
        rowIndex: product.rowIndex,
        value: product.value,
    };
};

const resolveProductLineCellEdit = (productLineCellEdit, incomingProduct) => {
    if (productLineCellEdit && typeof productLineCellEdit === 'object') {
        return productLineCellEdit;
    }
    if (
        incomingProduct &&
        typeof incomingProduct === 'object' &&
        !Array.isArray(incomingProduct) &&
        incomingProduct.field
    ) {
        return incomingProduct;
    }
    return undefined;
};

export const nextCopilotModifiedProductLines = ({
    copilotModifiedProductLines,
    current = {},
    incomingFields = {},
    previousData = {},
    productLineCellEdit,
} = {}) => {
    if (copilotModifiedProductLines !== undefined) {
        return sanitizeCopilotModifiedProductLines(copilotModifiedProductLines);
    }

    const next = sanitizeCopilotModifiedProductLines(current);
    const incoming = incomingFields || {};

    if (incoming.removeProducts) {
        const ids = Array.isArray(incoming.removeProducts)
            ? incoming.removeProducts
            : [incoming.removeProducts];
        ids.forEach((rowId) => {
            if (rowId != null && rowId !== '') delete next[rowId];
        });
    }

    const cellEdit = resolveProductLineCellEdit(productLineCellEdit, incoming.product);
    if (cellEdit?.field) {
        const rowId =
            cellEdit.id ||
            previousData.products?.[cellEdit.rowIndex]?.id ||
            (Array.isArray(incoming.products)
                ? incoming.products[cellEdit.rowIndex]?.id
                : undefined);
        const previousValue = Object.prototype.hasOwnProperty.call(cellEdit, 'previousValue')
            ? cellEdit.previousValue
            : getProductLineFieldValue(
                  (rowId != null && indexProductLinesById(previousData.products)[rowId]) ||
                      previousData.products?.[cellEdit.rowIndex],
                  cellEdit.field,
              );
        if (rowId && !areCrudFieldValuesEqual(previousValue, cellEdit.value)) {
            unmarkProductLineCell(next, rowId, cellEdit.field);
        }
    }

    if (Array.isArray(incoming.products)) {
        const kept = {};
        incoming.products.forEach((row) => {
            const rowId = row?.id;
            if (rowId == null || rowId === '' || !next[rowId]) return;
            kept[rowId] = next[rowId];
        });
        return kept;
    }

    return next;
};

export const isCopilotEnrichedProductLineCell = (
    copilotModifiedProductLines,
    rowId,
    fieldId,
    { readOnly = false } = {},
) => {
    if (!rowId || !fieldId || readOnly) return false;
    return Boolean(copilotModifiedProductLines?.[rowId]?.[fieldId]);
};
